import { useState } from 'react';
import { Button, Input, Modal } from '@/components/ui';
import { useToast } from '../../components/ui/Toast';
import { usePublishAgent } from '../../hooks/useAgents';
import type { Agent } from '../../types';

interface Props {
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * "Publish New Version" dialog. Extracted from AgentDetail so the agents list
 * can offer the same action from its row menu — both surfaces drive it through
 * `AgentActionsMenu`, so the two menus stay identical.
 */
export default function PublishAgentModal({ agent, isOpen, onClose }: Props) {
  const { success, error: showError } = useToast();
  const publishAgent = usePublishAgent();
  const [description, setDescription] = useState('');

  const handlePublish = async () => {
    try {
      await publishAgent.mutateAsync({
        agentId: agent.agentId,
        description: description || undefined,
      });
      success('Agent published successfully');
      setDescription('');
      onClose();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to publish agent');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Publish New Version"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handlePublish} isLoading={publishAgent.isPending}>
            Publish
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-muted-foreground">
          Publishing will create a new version ({(agent.latestVersion ?? 0) + 1})
          based on the current configuration.
        </p>
        <Input
          label="Version Description (optional)"
          placeholder="What changed in this version?"
          value={description}
          onChange={(e: { target: { value: string } }) => setDescription(e.target.value)}
        />
      </div>
    </Modal>
  );
}
